import { ChangeEvent, useState } from "react";
import { controlToastState, Modal, TextInput } from "risa-oasis-ui_v2";

import { POC_OPTIONS } from "../../../constants/submissionSummaryOptions";
import { PharmaStpFileModel } from "../../../data-model/nycbsPharmaOrder";
import { ModalId } from "../../../enums/modalId";
import { NycbsPharmaOrderKeys } from "../../../enums/nycbsPharmaOrder";
import { QaFilledBy } from "../../../enums/qaFilledBy";
import { SecondStpStatus } from "../../../enums/secondStpStatus";
import {
  getSubmissionSummaryDisplayKey,
  getSubmissionSummaryPayloadKey,
  isSubmissionSummaryKeyFieldDisabled,
  isSubmissionSummaryKeyFieldRequired,
  resolveSubmissionSummaryRouteVia,
  SubmissionSummaryRouteVia,
  SUBMISSION_SUMMARY_ROUTE_VIA_OPTIONS,
} from "../../../enums/submissionSummaryRouteVia";
import BadgeDropdown from "../../badgeDropdown/badgeDropdown";
import CustomRadioButton from "../../customRadioButton/customRadioButton";
import LabelValueRow from "../../labelValueRow/labelValueRow";
import { onSubmissionSave } from "./actionHandlers/onSubmissionSave";
import {
  SubmissionSummaryDataKeys,
  useSubmissionSummaryData,
} from "./hooks/useSubmissionSummaryData";
import {
  getNestedOptionsForCaseStatus,
  getNestedOptionsForSecondStpStatus,
} from "./utils/getNestedOptions";
import { getPocBadge } from "./utils/getPocBadge";

/**
 * Props for the submission summary modal (CMM key and status capture).
 */
interface SubmissionSummaryProps {
  onClose: () => void;
  metaData: {
    data: PharmaStpFileModel;
  };
  onSuccess?: () => void;
  /**
   * When true (any flow other than pharma STP file), hides Second STP Status and Case Status
   * and omits `second_stp_status` / `response_status` from the update payload.
   */
  hideQaSecondStpAndComments?: boolean;
  /**
   * When true (Delivery tab / pharma STP file), shows Call Required and Call reason fields
   * and persists them on save.
   */
  showCallFields?: boolean;
  /**
   * Enables the two-CTA footer used by the questionnaire "Submit Case" flow.
   *
   * "Send to Delivery" saves the submission summary only; "Submit and send to
   * delivery" fires `onSubmitCase` in parallel with the same save and reports
   * both outcomes through `onSubmitCaseComplete`.
   */
  submitCaseAction?: {
    /** Companion request; resolves `true` when it succeeded. */
    onSubmitCase: () => Promise<boolean>;
    /**
     * Fired once both requests settle, with whether everything succeeded and
     * whether the companion request itself succeeded.
     */
    onSubmitCaseComplete?: (
      bothSucceeded: boolean,
      isCaseSubmitted: boolean,
    ) => void;
  };
}

/**
 * Renders the submission summary modal for updating the CMM key and workflow fields before persisting to BigQuery.
 *
 * @param props - Modal callbacks, row `metaData`, optional `hideQaSecondStpAndComments`, and `showCallFields` for Delivery tab
 * @returns The modal UI
 */
const SubmissionSummary = (props: SubmissionSummaryProps) => {
  const { data } = props.metaData;
  const {
    hideQaSecondStpAndComments = false,
    showCallFields = false,
    submitCaseAction,
  } = props;
  const submissionKey =
    data?.[NycbsPharmaOrderKeys.CmmResultKey] || data?.covermymed_id || "";
  const baseKey = data?.[NycbsPharmaOrderKeys.CmmResultKey] || submissionKey;
  const initialRouteVia = resolveSubmissionSummaryRouteVia(data?.route_via);
  const getInitialKeyForRouteVia = (routeVia: SubmissionSummaryRouteVia) => {
    if (isSubmissionSummaryKeyFieldDisabled(routeVia)) {
      return getSubmissionSummaryPayloadKey(routeVia, baseKey, "");
    }
    if (routeVia === SubmissionSummaryRouteVia.CMM) {
      return getSubmissionSummaryDisplayKey(
        SubmissionSummaryRouteVia.CMM,
        baseKey,
        submissionKey,
      );
    }
    return "";
  };

  const [selectedRouteVia, setSelectedRouteVia] =
    useState<SubmissionSummaryRouteVia>(initialRouteVia);
  const [keyInputResetCounter, setKeyInputResetCounter] = useState(0);
  const isKeyFieldDisabled =
    isSubmissionSummaryKeyFieldDisabled(selectedRouteVia);
  const isKeyFieldRequired =
    isSubmissionSummaryKeyFieldRequired(selectedRouteVia);

  const {
    submissionSummaryData,
    handleSubmissionSummaryDataChange,
    handleSecondStpStatusChange,
    handleCallRequiredChange,
    handleKeyChange,
    isFormValid,
  } = useSubmissionSummaryData(
    getInitialKeyForRouteVia(initialRouteVia),
    data ? (data as unknown as Record<string, string>) : undefined,
    {
      hideQaSecondStpAndComments,
      showCallFields,
      isKeyFieldOptional: !isKeyFieldRequired && !isKeyFieldDisabled,
    },
  );
  const [resetCounter, setResetCounter] = useState<{
    secondStpStatus: number;
    caseStatus: number;
  }>({
    secondStpStatus: 0,
    caseStatus: 0,
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmittingCase, setIsSubmittingCase] = useState<boolean>(false);

  const handleRouteViaChange = (routeVia: SubmissionSummaryRouteVia) => {
    setSelectedRouteVia(routeVia);
    if (isSubmissionSummaryKeyFieldDisabled(routeVia)) {
      handleKeyChange(getSubmissionSummaryPayloadKey(routeVia, baseKey, ""));
    } else if (routeVia === SubmissionSummaryRouteVia.CMM) {
      handleKeyChange(
        getSubmissionSummaryDisplayKey(routeVia, baseKey, baseKey),
      );
    } else {
      handleKeyChange("");
    }
    setKeyInputResetCounter((prev) => prev + 1);
    setResetCounter((prev) => ({
      secondStpStatus: hideQaSecondStpAndComments
        ? prev.secondStpStatus
        : prev.secondStpStatus + 1,
      caseStatus: hideQaSecondStpAndComments
        ? prev.caseStatus
        : prev.caseStatus + 1,
    }));
  };

  /**
   * Persists the submission summary and reports whether the call succeeded,
   * leaving toasts / closing to the caller so it can be combined with other
   * in-flight requests.
   *
   * @returns `true` when the update succeeded
   */
  const runSubmissionSave = async (): Promise<boolean> => {
    let isSaved = false;
    await onSubmissionSave({
      data,
      key: getSubmissionSummaryPayloadKey(
        selectedRouteVia,
        baseKey,
        submissionSummaryData.key,
      ),
      poc: submissionSummaryData.poc,
      omitHiddenWorkflowFields: hideQaSecondStpAndComments,
      callRequired: showCallFields
        ? submissionSummaryData.callRequired
        : undefined,
      callReason: showCallFields
        ? submissionSummaryData.callRequired === "yes"
          ? submissionSummaryData.callReason.trim()
          : ""
        : undefined,
      caseStatus: hideQaSecondStpAndComments
        ? undefined
        : submissionSummaryData.caseStatus,
      secondStpStatus: hideQaSecondStpAndComments
        ? undefined
        : submissionSummaryData.secondStpStatus,
      routeVia: selectedRouteVia,
      onSuccess: () => {
        isSaved = true;
      },
      onError: (error) => {
        console.error("Error saving submission:", error);
      },
    });
    return isSaved;
  };

  const handleSave = async () => {
    setIsLoading(true);
    const isSaved = await runSubmissionSave();
    setIsLoading(false);
    if (isSaved) {
      controlToastState("submission-summary-save-success");
      props.onSuccess?.();
      props.onClose();
      return;
    }
    controlToastState("submission-summary-save-error");
  };

  /**
   * Fires the submission summary save and the companion "Submit Case" request
   * in parallel, then closes the modal once both have succeeded.
   *
   * Exactly one toast is raised per outcome: the success and submit-failure
   * toasts belong to `onSubmitCaseComplete`, so only a save failure is
   * announced from here.
   */
  const handleSubmitAndSendToDelivery = async () => {
    if (!submitCaseAction) return;
    setIsSubmittingCase(true);
    const [isSaved, isCaseSubmitted] = await Promise.all([
      runSubmissionSave(),
      submitCaseAction.onSubmitCase(),
    ]);
    setIsSubmittingCase(false);
    const bothSucceeded = isSaved && isCaseSubmitted;
    if (bothSucceeded) {
      props.onSuccess?.();
      props.onClose();
    } else if (!isSaved) {
      controlToastState("submission-summary-save-error");
    }
    submitCaseAction.onSubmitCaseComplete?.(bothSucceeded, isCaseSubmitted);
  };

  const saveButtonText = submitCaseAction
    ? isSubmittingCase
      ? "Submitting..."
      : "Submit and send to delivery"
    : isLoading
      ? "Submitting..."
      : "Submit";

  return (
    <Modal
      dialogId={ModalId.SUBMISSION_SUMMARY_MODAL}
      title={"Submission Summary"}
      saveButtonText={saveButtonText}
      cancelText={submitCaseAction ? "Send to Delivery" : "Cancel"}
      onCancel={submitCaseAction ? handleSave : props.onClose}
      onClose={props.onClose}
      onSave={submitCaseAction ? handleSubmitAndSendToDelivery : handleSave}
      disableSave={!isFormValid || isLoading || isSubmittingCase}
    >
      <div className="flex flex-col gap-1">
        <div className="flex flex-col gap-2">
          <LabelValueRow label="MRN:">
            <div className="text-sm font-normal text-primaryGray-2">
              {data?.[NycbsPharmaOrderKeys.PatientMrn] ?? data?.patient_mrn}
            </div>
          </LabelValueRow>

          <LabelValueRow label="Drug:">
            <div className="text-sm font-normal text-primaryGray-2">
              {data?.[NycbsPharmaOrderKeys.DrugNameOncoEmr] || data?.drug}
            </div>
          </LabelValueRow>

          <LabelValueRow
            label={
              <>
                Key:
                {isKeyFieldRequired && <span className="text-red-500"> *</span>}
              </>
            }
          >
            <div className="flex w-full flex-row items-center gap-2">
              <BadgeDropdown
                badgeList={SUBMISSION_SUMMARY_ROUTE_VIA_OPTIONS}
                defaultBadge={
                  SUBMISSION_SUMMARY_ROUTE_VIA_OPTIONS.find(
                    (badge) => badge.id === selectedRouteVia,
                  ) ??
                  SUBMISSION_SUMMARY_ROUTE_VIA_OPTIONS.find(
                    (badge) => badge.id === SubmissionSummaryRouteVia.CMM,
                  )
                }
                showBorder={true}
                borderColor="#A8A8A8"
                onClick={(id: string) => {
                  handleRouteViaChange(id as SubmissionSummaryRouteVia);
                }}
              />
              <div
                className={`min-w-0 flex-1 ${isKeyFieldDisabled ? "opacity-60" : ""}`}
              >
                <TextInput
                  key={`key-input-${keyInputResetCounter}`}
                  id="key-input"
                  label="Key"
                  hideLabel={true}
                  defaultValue={
                    isKeyFieldDisabled
                      ? ""
                      : selectedRouteVia === SubmissionSummaryRouteVia.CMM
                        ? getSubmissionSummaryDisplayKey(
                            SubmissionSummaryRouteVia.CMM,
                            baseKey,
                            submissionSummaryData.key,
                          )
                        : submissionSummaryData.key
                  }
                  readOnly={isKeyFieldDisabled}
                  required={isKeyFieldRequired}
                  onChange={(e) => {
                    handleKeyChange(e.value);
                    setResetCounter((prev) => ({
                      secondStpStatus: hideQaSecondStpAndComments
                        ? prev.secondStpStatus
                        : prev.secondStpStatus + 1,
                      caseStatus: hideQaSecondStpAndComments
                        ? prev.caseStatus
                        : prev.caseStatus + 1,
                    }));
                  }}
                />
              </div>
            </div>
          </LabelValueRow>
        </div>

        <div className="flex flex-col justify-center gap-2">
          {!hideQaSecondStpAndComments && (
            <LabelValueRow label="Second STP Status:">
              <BadgeDropdown
                badgeList={getNestedOptionsForSecondStpStatus(
                  submissionSummaryData.qaFilledBy as QaFilledBy,
                )}
                defaultBadge={getNestedOptionsForSecondStpStatus(
                  submissionSummaryData.qaFilledBy as QaFilledBy,
                ).find(
                  (badge) => badge.id === submissionSummaryData.secondStpStatus,
                )}
                showBorder={true}
                borderColor="#A8A8A8"
                onClick={(id: string) => {
                  handleSecondStpStatusChange(id);
                  setResetCounter((prev) => ({
                    ...prev,
                    caseStatus: prev.caseStatus + 1,
                  }));
                }}
                resetCounter={resetCounter.secondStpStatus}
              />
            </LabelValueRow>
          )}

          {!hideQaSecondStpAndComments && (
            <LabelValueRow label="Case Status:">
              <BadgeDropdown
                badgeList={getNestedOptionsForCaseStatus(
                  submissionSummaryData.secondStpStatus as SecondStpStatus,
                )}
                defaultBadge={getNestedOptionsForCaseStatus(
                  submissionSummaryData.secondStpStatus as SecondStpStatus,
                ).find(
                  (badge) => badge.id === submissionSummaryData.caseStatus,
                )}
                showBorder={true}
                borderColor="#A8A8A8"
                onClick={(id: string) => {
                  handleSubmissionSummaryDataChange(
                    SubmissionSummaryDataKeys.CASE_STATUS,
                    id,
                  );
                }}
                resetCounter={resetCounter.caseStatus}
              />
            </LabelValueRow>
          )}

          <LabelValueRow label="POC:" showDivider={showCallFields}>
            <BadgeDropdown
              badgeList={POC_OPTIONS}
              defaultBadge={getPocBadge(submissionSummaryData.poc)}
              showBorder={true}
              borderColor="#A8A8A8"
              onClick={(id: string) => {
                handleSubmissionSummaryDataChange(
                  SubmissionSummaryDataKeys.POC,
                  id,
                );
              }}
            />
          </LabelValueRow>

          {showCallFields && (
            <>
              <LabelValueRow label="Call Required:">
                <div className="flex flex-row flex-wrap items-center gap-4">
                  <CustomRadioButton
                    name="submission-summary-call-required"
                    value="yes"
                    checked={submissionSummaryData.callRequired === "yes"}
                    label="Yes"
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleCallRequiredChange(e.target.value)
                    }
                  />
                  <CustomRadioButton
                    name="submission-summary-call-required"
                    value="no"
                    checked={submissionSummaryData.callRequired === "no"}
                    label="No"
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleCallRequiredChange(e.target.value)
                    }
                  />
                </div>
              </LabelValueRow>

              <LabelValueRow label="Call reason:" showDivider={false}>
                <TextInput
                  id="submission-summary-call-reason-input"
                  label="Call reason"
                  hideLabel={true}
                  defaultValue={submissionSummaryData.callReason}
                  onChange={(e) =>
                    handleSubmissionSummaryDataChange(
                      SubmissionSummaryDataKeys.CALL_REASON,
                      e.value,
                    )
                  }
                />
              </LabelValueRow>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default SubmissionSummary;
